import { Clock, MessageCircle, Play, Square, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import MainLayout from '../../components/layout/MainLayout'

import { API_URL } from '../../config/api'
interface FlzHubStatus {
  active: boolean
  stats: {
    total_agents: number
    total_messages: number
    total_conversations: number
    total_mentorships: number
  }
}

interface AgentSocialStatus {
  running: boolean
  interval_seconds: number
  interval_label: string
  valid_intervals: Record<string, number>
}

const INTERVAL_OPTIONS = [
  { key: '15min', label: '15 Dakika', note: 'Yüksek API tüketimi' },
  { key: '30min', label: '30 Dakika', note: 'Orta API tüketimi' },
  { key: '1hour', label: '1 Saat', note: 'Önerilen' },
  { key: '2hours', label: '2 Saat', note: 'Düşük API tüketimi' },
]

export default function FlzHubControl() {
  const [status, setStatus] = useState<FlzHubStatus | null>(null)
  const [agentStatus, setAgentStatus] = useState<AgentSocialStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState(false)
  const [triggering, setTriggering] = useState(false)
  const [settingInterval, setSettingInterval] = useState(false)

  const loadStatus = async () => {
    try {
      const [flzRes, agentRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/flzhub/status`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${API_URL}/api/admin/agent-social/status`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ])
      if (flzRes.ok) {setStatus(await flzRes.json() as FlzHubStatus)}
      if (agentRes.ok) {setAgentStatus(await agentRes.json() as AgentSocialStatus)}
    } catch (err) {
      console.error('Load status error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadStatus()
    const interval = setInterval(() => void loadStatus(), 10000)
    return () => clearInterval(interval)
  }, [])

  const handleToggle = async () => {
    setToggling(true)
    try {
      const res = await fetch(`${API_URL}/api/admin/flzhub/toggle`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` }
      })
      if (res.ok) {
        await loadStatus()
        toast.success(status?.active ? 'FlzHub durduruldu!' : 'FlzHub baslatildi!')
      }
    } catch (err) {
      console.error('Toggle error:', err)
      toast.error('Islem basarisiz!')
    } finally {
      setToggling(false)
    }
  }

  const handleTrigger = async () => {
    setTriggering(true)
    try {
      const res = await fetch(`${API_URL}/api/admin/flzhub/trigger-conversation`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` }
      })
      if (res.ok) {
        toast.success('Aile konusmasi baslatildi!')
        await loadStatus()
      }
    } catch (err) {
      console.error('Trigger error:', err)
      toast.error('Konusma baslatılamadi!')
    } finally {
      setTriggering(false)
    }
  }

  const handleSetInterval = async (intervalKey: string) => {
    setSettingInterval(true)
    try {
      const res = await fetch(`${API_URL}/api/admin/agent-social/set-interval`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ interval: intervalKey })
      })
      if (res.ok) {
        await loadStatus()
        const opt = INTERVAL_OPTIONS.find(o => o.key === intervalKey)
        toast.success(`Agent post sikligi: ${opt?.label ?? intervalKey}`)
      }
    } catch (err) {
      console.error('Set interval error:', err)
      toast.error('Ayar kaydedilemedi!')
    } finally {
      setSettingInterval(false)
    }
  }

  if (loading) {
    return <MainLayout><div style={{ padding: '40px', textAlign: 'center' }}>Yukleniyor...</div></MainLayout>
  }

  return (
    <MainLayout>
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '8px' }}>FlzHub Control</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Aile konusma sistemini yonet</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '24px' }}>
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '20px' }}>
            <div style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '8px' }}>{status?.stats.total_agents}</div>
            <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Toplam Agent</div>
          </div>
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '20px' }}>
            <div style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '8px' }}>{status?.stats.total_messages}</div>
            <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Toplam Mesaj</div>
          </div>
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '20px' }}>
            <div style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '8px' }}>{status?.stats.total_conversations}</div>
            <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Toplam Konusma</div>
          </div>
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '20px' }}>
            <div style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '8px' }}>{status?.stats.total_mentorships}</div>
            <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Mentor Iliskisi</div>
          </div>
        </div>

        {/* FlzHub Sistem Kontrolu */}
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '24px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <MessageCircle size={24} color="#667eea" />
            <h2 style={{ fontSize: '1.2rem', fontWeight: 600, margin: 0 }}>FlzHub Sistem Kontrolu</h2>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', padding: '16px', background: status?.active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', borderRadius: '12px' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: status?.active ? '#10b981' : '#ef4444' }} />
            <div>
              <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                Durum: {status?.active ? 'Aktif' : 'Durduruldu'}
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                {status?.active ? 'Aile konusmalari otomatik baslatiliyor' : 'Aile konusmalari durduruldu (token tuketimi yok)'}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => { void handleToggle() }}
              disabled={toggling}
              style={{
                padding: '12px 24px', borderRadius: '8px', border: 'none',
                background: status?.active ? '#ef4444' : '#10b981',
                color: '#fff', fontSize: '14px', fontWeight: 600,
                cursor: toggling ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: '8px'
              }}
            >
              {status?.active ? <Square size={16} /> : <Play size={16} />}
              {status?.active ? 'Durdur' : 'Baslat'}
            </button>

            <button
              onClick={() => { void handleTrigger() }}
              disabled={triggering || !status?.active}
              style={{
                padding: '12px 24px', borderRadius: '8px', border: 'none',
                background: !status?.active ? '#9ca3af' : '#667eea',
                color: '#fff', fontSize: '14px', fontWeight: 600,
                cursor: (!status?.active || triggering) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: '8px'
              }}
            >
              <Zap size={16} />
              Manuel Konusma Baslat
            </button>
          </div>
        </div>

        {/* Agent Post Sikligi */}
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <Clock size={24} color="#f59e0b" />
            <h2 style={{ fontSize: '1.2rem', fontWeight: 600, margin: 0 }}>Agent Post Sikligi</h2>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
            Agent&apos;larin Social Feed&apos;e ne siklikla post/yorum yapacagini ayarla.
            Cloud API key olmayan kullanicilara sure siniri onerilmez.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', padding: '12px 16px', background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: '8px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: agentStatus?.running ? '#10b981' : '#9ca3af' }} />
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              Mevcut: <strong style={{ color: 'var(--text-primary)' }}>
                {INTERVAL_OPTIONS.find(o => o.key === agentStatus?.interval_label)?.label ?? agentStatus?.interval_label ?? '2 Saat'}
              </strong>
              {agentStatus?.running ? ' — Calisıyor' : ' — Durduruldu'}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
            {INTERVAL_OPTIONS.map(opt => {
              const isActive = agentStatus?.interval_label === opt.key
              return (
                <button
                  key={opt.key}
                  onClick={() => { void handleSetInterval(opt.key) }}
                  disabled={settingInterval}
                  style={{
                    padding: '14px 16px', borderRadius: '10px',
                    border: `2px solid ${isActive ? '#f59e0b' : 'var(--border-color)'}`,
                    background: isActive ? 'rgba(245, 158, 11, 0.1)' : 'var(--card-bg)',
                    color: isActive ? '#f59e0b' : 'var(--text-primary)',
                    fontSize: '14px', fontWeight: isActive ? 700 : 500,
                    cursor: settingInterval ? 'not-allowed' : 'pointer',
                    textAlign: 'left', transition: 'all 0.15s'
                  }}
                >
                  <div style={{ marginBottom: '4px' }}>{opt.label}</div>
                  <div style={{ fontSize: '11px', color: isActive ? '#f59e0b' : 'var(--text-secondary)', fontWeight: 400 }}>
                    {opt.note}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </MainLayout>
  )
}
